import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { Award, Loader, AlertCircle, ArrowLeft, Share2, Printer, Star, Medal, CheckCircle } from "lucide-react";
import { useReactToPrint } from "react-to-print";
import { certificateApi } from "../lib";

interface Certificate {
  _id: string;
  user: {
    firstName: string;
    lastName: string;
    email?: string;
  };
  course: {
    _id: string;
    title: string;
    description?: string;
  };
  certificateId: string;
  issuedAt: string;
  score?: number;
}

export default function CertificateViewPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const courseId = searchParams.get("courseId");
  const [certificate, setCertificate] = useState<Certificate | null>(null);
  const [loading, setLoading] = useState(true);
  const certificateRef = useRef<HTMLDivElement>(null);

  const handlePrint = useReactToPrint({
    contentRef: certificateRef,
    documentTitle: `Certificate_${certificate?.course?.title || "Completion"}_${certificate?.user?.firstName || ""}`,
    pageStyle: `
      @page {
        size: A4;
        margin: 0;
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          margin: 0;
          padding: 0;
        }
        .no-print {
          display: none !important;
        }
        .print-container {
          margin: 0;
          padding: 0;
        }
      }
    `,
  });

  const handleBack = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      navigate("/dashboard/student/certificates");
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Certificate of Completion - ${certificate?.course?.title}`,
          text: `I successfully completed "${certificate?.course?.title}" on LearnHub! 🎓`,
          url: window.location.href,
        });
      } catch (err) {
        console.log("Share cancelled");
      }
    } else {
      try {
        await navigator.clipboard.writeText(window.location.href);
        alert("Link copied to clipboard!");
      } catch (err) {
        alert("Unable to copy link");
      }
    }
  };

  useEffect(() => {
    const fetchCertificate = async () => {
      if (!courseId) {
        setLoading(false);
        return;
      }
      try {
        const res = await certificateApi.getMyCertificates();
        if (res?.success && Array.isArray(res.data)) {
          const found = res.data.find((c: any) => {
            const cid = c.course?._id || c.course;
            return cid === courseId;
          }) || res.data.find((c: any) => c.course === courseId);
          if (found) {
            setCertificate(found);
            setLoading(false);
            return;
          }
        }
        setCertificate(null);
      } catch (err) {
        console.error("Certificate fetch error:", err);
        setCertificate(null);
      } finally {
        setLoading(false);
      }
    };

    fetchCertificate();
  }, [courseId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <Loader className="w-8 h-8 mx-auto mb-3 text-orange-500 animate-spin" />
          <p className="text-sm text-gray-500">Loading certificate...</p>
        </div>
      </div>
    );
  }

  if (!courseId) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 bg-gray-50">
        <div className="max-w-md text-center">
          <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="mb-2 text-xl font-semibold text-gray-900">Missing Course ID</h2>
          <p className="mb-6 text-gray-600">Please provide a valid course ID to view your certificate.</p>
          <Link
            to="/dashboard/student/certificates"
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white transition-all rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md"
          >
            View all certificates
          </Link>
        </div>
      </div>
    );
  }

  if (!certificate) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 bg-gray-50">
        <div className="max-w-md text-center">
          <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-amber-100">
            <Award className="w-8 h-8 text-amber-500" />
          </div>
          <h2 className="mb-2 text-xl font-semibold text-gray-900">Certificate not found</h2>
          <p className="mb-6 text-gray-600">Complete all lessons and pass the required exam to receive your certificate.</p>
          <div className="flex flex-col gap-3">
            <Link
              to={`/course/${courseId}`}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white transition-all rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Course
            </Link>
            <Link
              to="/dashboard/student/certificates"
              className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 transition-all bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              My Certificates
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 bg-gray-100 no-print">
      <div className="container max-w-4xl px-4 mx-auto">
        {/* Header Buttons */}
        <div className="flex items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center flex-shrink-0">
              <div className="flex items-center gap-0">
                <span className="text-lg font-bold text-gray-900">Learn</span>
                <span className="text-lg font-bold text-orange-500">Hub</span>
              </div>
            </Link>
            <button
              onClick={handleBack}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 transition-all bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 hover:text-orange-600"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white transition-all rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md"
            >
              <Printer className="w-4 h-4" />
              <span className="hidden sm:inline">Print</span>
            </button>
            <button
              onClick={handleShare}
              className="p-2 text-gray-700 transition-all bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 hover:text-orange-600"
              title="Share"
            >
              <Share2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Certificate - A5 Size Document */}
        <div ref={certificateRef} className="print-container">
          <div className="overflow-hidden bg-white rounded-lg shadow-2xl" style={{ maxWidth: '600px', margin: '0 auto' }}>
            {/* Certificate Inner Border */}
            <div className="m-4 overflow-hidden border-2 rounded-lg border-amber-200 md:m-6">
              {/* Decorative Border */}
              <div className="relative bg-gradient-to-br from-white via-amber-50 to-white">
                {/* Corner Decorations */}
                <div className="absolute top-0 left-0 w-12 h-12 border-t-4 border-l-4 rounded-tl-lg border-amber-400"></div>
                <div className="absolute top-0 right-0 w-12 h-12 border-t-4 border-r-4 rounded-tr-lg border-amber-400"></div>
                <div className="absolute bottom-0 left-0 w-12 h-12 border-b-4 border-l-4 rounded-bl-lg border-amber-400"></div>
                <div className="absolute bottom-0 right-0 w-12 h-12 border-b-4 border-r-4 rounded-br-lg border-amber-400"></div>

                {/* Content */}
                <div className="p-6 text-center md:p-8">
                  {/* Top Corner Brand */}
                  <div className="absolute top-3 left-3">
                    <span className="text-xs font-bold text-gray-400">Learn</span>
                    <span className="text-xs font-bold text-orange-400">Hub</span>
                  </div>

                  {/* Top Icon */}
                  <div className="flex justify-center mb-4">
                    <div className="p-2 rounded-full bg-gradient-to-br from-amber-100 to-orange-100">
                      <Medal className="w-10 h-10 text-orange-500 md:w-12 md:h-12" />
                    </div>
                  </div>

                  {/* Title */}
                  <h1 className="mb-1 text-lg font-bold tracking-wide text-gray-800 uppercase md:text-xl">
                    Certificate of Completion
                  </h1>
                  <div className="w-16 h-px mx-auto my-2 bg-gradient-to-r from-transparent via-amber-400 to-transparent"></div>

                  {/* Body */}
                  <p className="mt-4 text-xs text-gray-500 md:text-sm">This certificate is proudly presented to</p>
                  
                  <h2 className="mt-2 text-xl font-bold text-gray-900 md:text-2xl">
                    {certificate.user?.firstName} {certificate.user?.lastName}
                  </h2>
                  
                  <p className="mt-4 text-xs text-gray-600 md:text-sm">for successfully completing the course</p>
                  
                  <h3 className="mt-2 text-base font-semibold text-gray-800 md:text-lg">
                    {certificate.course?.title}
                  </h3>

                  {/* Score */}
                  {certificate.score && (
                    <div className="inline-flex items-center gap-2 px-3 py-1 mt-4 rounded-full bg-green-50">
                      <CheckCircle className="w-3 h-3 text-green-600" />
                      <span className="text-xs font-medium text-green-700">Score: {certificate.score}%</span>
                    </div>
                  )}

                  {/* Stars */}
                  <div className="flex justify-center gap-1 my-4">
                    {[...Array(3)].map((_, i) => (
                      <Star key={i} className="w-3 h-3 text-amber-400 fill-amber-400 md:w-4 md:h-4" />
                    ))}
                  </div>

                  {/* Details */}
                  <div className="max-w-sm mx-auto mt-4">
                    <div className="text-xs text-gray-500">
                      <p>Issued on: <span className="font-medium text-gray-700">
                        {new Date(certificate.issuedAt).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </span></p>
                      <p className="mt-1">Certificate ID: <span className="font-mono text-xs text-amber-600">
                        {certificate.certificateId}
                      </span></p>
                    </div>
                  </div>

                  {/* Signature */}
                  <div className="grid max-w-xs grid-cols-2 gap-4 pt-4 mx-auto mt-6 border-t border-gray-200">
                    <div>
                      <div className="w-full h-px mb-1 rounded-full bg-amber-300"></div>
                      <p className="text-[10px] text-gray-500">Authorized Signatory</p>
                    </div>
                    <div>
                      <div className="w-full h-px mb-1 rounded-full bg-amber-300"></div>
                      <p className="text-[10px] text-gray-500">LearnHub Official</p>
                    </div>
                  </div>

                  {/* Verification */}
                  <p className="mt-4 text-[9px] text-gray-400">
                    Verify at {window.location.origin}/verify-certificate/{certificate.certificateId}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}